import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { IconButton, TextField as MuiTextField } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { Clear } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { convertToInteger } from "../../utils/string";
import { Tooltip } from "../Tooltip";

const useStyles = makeStyles(theme => ({
  clearButton: {
    backgroundColor: theme.palette.primary.main,
    position: "absolute",
    padding: "7px",
    right: "-3px",
    top: "-3px",
    height: 0,
    width: 0,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  clearButtonLabel: {
    color: theme.palette.common.white
  },
  clearButtonIcon: {
    fontSize: "0.75rem"
  }
}));

const TextField = ({
  InputProps,
  onDelete,
  type,
  disabled,
  tooltip,
  variant = "filled",
  ...textFieldProps
}) => {
  const classes = useStyles();
  const [{ value }, { error }, { setValue }] = useField(textFieldProps);

  const { submitCount, isSubmitting } = useFormikContext(textFieldProps);
  const [focused, setFocused] = useState(false);
  const [shrinkInput, setShrinkInput] = useState(false);

  useEffect(() => {
    if (value && type === "number") {
      setValue(convertToInteger(value));
    }
  }, [value]);

  useEffect(() => {
    setShrinkInput(!!`${value}` || focused);
  }, [value, focused]);

  const showError = !!error && submitCount > 0;

  const textField = (
    <MuiTextField
      variant={variant}
      fullWidth
      helperText={showError && error}
      value={value}
      onChange={({ target: { value } }) => {
        setValue(value);
      }}
      InputProps={{
        ...InputProps,
        endAdornment: onDelete ? (
          <IconButton
            onClick={onDelete}
            classes={{
              root: classes.clearButton,
              label: classes.clearButtonLabel
            }}
            size="large"
          >
            <Clear className={classes.clearButtonIcon} />
          </IconButton>
        ) : null,
        onBlur: () => setFocused(false)
      }}
      InputLabelProps={{ shrink: shrinkInput }}
      onFocus={() => setFocused(true)}
      error={showError && !!error}
      FormHelperTextProps={{
        "data-qa": `helper-text-${textFieldProps?.name}`
      }}
      disabled={isSubmitting || disabled}
      {...textFieldProps}
    />
  );

  return !!tooltip ? <Tooltip title={tooltip}>{textField}</Tooltip> : textField;
};

TextField.propTypes = {
  textFieldProps: PropTypes.object,
  onDelete: PropTypes.func,
  type: PropTypes.oneOf(["text", "number"]),
  disabled: PropTypes.bool
};

export default TextField;
